import { useEffect, useMemo, useRef } from 'react';

import { appInsights } from '../../../core/analytics/applicationInsights';
import { closeAssistanceEvent } from '../../../core/analytics/customEvents';

interface SessionState {
    recordId: string;
    sessionId: string;
    accumulatedMs: number;
    runningSince: number | null;
    lastFlushedSeconds: number;
    finished: boolean;
}

interface SessionContext {
    user: any;
    record: any;
    document: any;
}

export interface AssistanceSessionProps {
    sessionId: string;
    activeDurationSeconds: number;
}

export interface AssistanceSession {
    getSessionProps: (record?: any) => AssistanceSessionProps;
    finish: (record?: any) => AssistanceSessionProps;
    restart: () => void;
}

const isPageActive = () => window.document.visibilityState === 'visible' && window.document.hasFocus();

const calculateActiveDurationSeconds = (session: SessionState) =>
    Math.round(
        (session.accumulatedMs + (session.runningSince != null ? performance.now() - session.runningSince : 0)) / 1000
    );

/**
 * Tracks how long a user actively works on a record in the assistance view: a session (one record-open,
 * identified by `sessionId`) accumulates time only while the tab is visible and focused. The cumulative
 * duration is flushed as an idempotent CLOSE_ASSISTANCE snapshot whenever the page becomes hidden and when
 * the record is left without finishing; FINISH_ASSISTANCE carries the final duration via `finish`.
 * Analytics sums these per-session durations (max snapshot per sessionId) into the assistance time KPI.
 */
const useAssistanceSession = ({ user, record, document }: SessionContext): AssistanceSession => {
    const sessionRef = useRef<SessionState | null>(null);
    const contextRef = useRef<SessionContext>({ user, record, document });

    useEffect(() => {
        contextRef.current = { user, record, document };
    });

    const pause = () => {
        const session = sessionRef.current;
        if (session?.runningSince != null) {
            session.accumulatedMs += performance.now() - session.runningSince;
            session.runningSince = null;
        }
    };

    const resume = () => {
        const session = sessionRef.current;
        if (session != null && !session.finished && session.runningSince == null && isPageActive()) {
            session.runningSince = performance.now();
        }
    };

    const ensureSession = (sessionRecord?: any): SessionState => {
        const recordId = (sessionRecord ?? contextRef.current.record)?.id;
        if (sessionRef.current?.recordId !== recordId) {
            sessionRef.current = {
                recordId,
                sessionId: crypto.randomUUID(),
                accumulatedMs: 0,
                runningSince: isPageActive() ? performance.now() : null,
                lastFlushedSeconds: 0,
                finished: false,
            };
        }
        return sessionRef.current;
    };

    const pauseAndFlush = () => {
        pause();

        const session = sessionRef.current;
        const context = contextRef.current;
        if (session == null || session.finished || context.record?.id !== session.recordId) return;

        if (context.record?.stepRun?.executionStatus !== 'WAITING_FOR_MANUAL') return;

        const seconds = calculateActiveDurationSeconds(session);
        if (seconds <= session.lastFlushedSeconds) return;
        session.lastFlushedSeconds = seconds;
        appInsights?.trackEvent(
            ...closeAssistanceEvent(context.user, context.record, context.document, {
                sessionId: session.sessionId,
                activeDurationSeconds: seconds,
            })
        );
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (window.document.visibilityState === 'hidden') {
                pauseAndFlush();
            } else {
                resume();
            }
        };

        window.document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', pauseAndFlush);
        window.addEventListener('focus', resume);
        window.addEventListener('blur', pause);
        return () => {
            window.document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', pauseAndFlush);
            window.removeEventListener('focus', resume);
            window.removeEventListener('blur', pause);
        };
    }, []);

    // flush the session when the user leaves the record (navigation within the app / unmount)
    useEffect(() => {
        if (record?.id == null) return undefined;
        return pauseAndFlush;
    }, [record?.id]);

    return useMemo(() => {
        const getSessionProps = (sessionRecord?: any): AssistanceSessionProps => {
            const session = ensureSession(sessionRecord);
            return { sessionId: session.sessionId, activeDurationSeconds: calculateActiveDurationSeconds(session) };
        };

        return {
            getSessionProps,
            finish: (sessionRecord?: any) => {
                pause();
                const props = getSessionProps(sessionRecord);
                if (sessionRef.current != null) {
                    sessionRef.current.finished = true;
                }
                return props;
            },
            restart: () => {
                sessionRef.current = null;
                ensureSession();
            },
        };
    }, []);
};

export default useAssistanceSession;
